import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, inject, OnInit, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Format } from 'src/app/developer/models/format';
import { FormatType } from 'src/app/developer/models/format-type';
import { Variant } from 'src/app/developer/models/variant';
import { VariantVersion } from 'src/app/developer/models/variant-version';
import { DeveloperService } from 'src/app/developer/services/developer.service';
import { DistributionObjectService } from 'src/app/distribution/services';
import { Direction, DocumentTypeEntity, DocumentConfig, DocumentConfigSearchDisplay } from 'src/app/distribution/models';
import { DocumentConfigSelectorSimpleComponent } from 'src/app/distribution/document-config-selector-simple.component';
import { InitConfiguration } from '../models/init-configuration';
import { ModalDismissStatus } from '../models/modal-dismiss-status';
import { PartnerSelectorComponent } from '../../partner/partner-selector.component';
import { PartnerService } from '../../partner/services/partner.service';
import { InitializeService } from '../services/initialize.service';
import { InitStep } from '../models/init-step';
import { DataFieldGroup } from '../../shared/datafields/models';
import { PartnerSystemService } from 'src/app/partner/services/partner-system.service';
import { PartnerSystem } from 'src/app/partner/models';
import { DistributionSelectorMultipleComponent } from '../distribution-selector-multiple/distribution-selector-multiple.component';
import { DocumentConfigSelectorSimpleComponent as DocumentConfigSelectorSimpleComponent_1 } from '../document-config-selector-simple.component';
import { PartnerSelectorMultipleComponent } from '../../partner/partner-selector-multiple/partner-selector-multiple.component';
import { DataFieldGroupContainerComponent } from '../../shared/datafields/datafield-group-container.component';

interface InitConfigurationFormValue {
    name: string;
    sender: {
        partnerId: number | null;
    };
    receiver: {
        partnerIds: number[];
    };
    distribution: {
        distributionIds: number[];
        documentTypeId: number | null;
        formatId: number | null;
        variantId: number | null;
        variantVersionId: number | null;
        formatTypeId: number | null;
    };
    document: {
        documentNumberMacro: string | null;
    };
    disassemble: {
        preDisassembleInitStepId: number | null;
        customDisassembleInitStepId: number | null;
        postDisassembleInitStepId: number | null;
    };
    partnerSystem: {
        partnerSystemId: number | null;
    };
}

@Component({
    templateUrl: './init-configuration-dialog.component.html',
    styleUrls: ['./init-configuration-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ReactiveFormsModule, DistributionSelectorMultipleComponent, DocumentConfigSelectorSimpleComponent_1, PartnerSelectorComponent, PartnerSelectorMultipleComponent, DataFieldGroupContainerComponent]
})
export class InitConfigurationDialogComponent implements OnInit {
    readonly modal = inject(NgbActiveModal);
    private readonly fb = inject(UntypedFormBuilder);
    private readonly documentTypeService = inject(DistributionObjectService);
    private readonly initializeService = inject(InitializeService);
    private readonly initStepService = inject(InitializeService);
    private readonly partnerService = inject(PartnerService);
    private readonly developerService = inject(DeveloperService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly partnerSystemService = inject(PartnerSystemService);
    private readonly destroyRef = inject(DestroyRef);


    readonly Direction = Direction;
    formData: UntypedFormGroup = null;
    initConfiguration: InitConfiguration;
    headline: string;
    disabled = false;
    showDelete = false;
    distributionsChosen = false;
    documentConfigChosen = false;
    documentTypes: DocumentTypeEntity[] = [];
    formatTypes: FormatType[];
    initSteps: InitStep[] = [];
    preDisassembleDisplayGroups: DataFieldGroup[] = [];
    customDisassembleDisplayGroups: DataFieldGroup[] = [];
    postDisassembleDisplayGroups: DataFieldGroup[] = [];
    partnerSystems: PartnerSystem[] = [];
    selectedDocumentType: DocumentTypeEntity;

    private readonly docConfigIn = viewChild.required<DocumentConfigSelectorSimpleComponent>('docConfigInSimple');
    private readonly senderPartnerSelector = viewChild.required<PartnerSelectorComponent>('senderPartnerSelector');

    ngOnInit(): void {
        void this.initialize();
    }

    private async initialize(): Promise<void> {
        this.formData = this.buildForm();

        await this.refresh();

        this.updateFormFromInitConfiguration();
        await this.updateDocumentConfigChosen();
        this.setSelectedDocumentConfig();

        this.docConfigIn().initialized.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            this.setSelectedDocumentConfig();
        });

        if (this.initConfiguration?.SenderPartnerId) {
            const partner = await this.partnerService.getPartner(this.initConfiguration.SenderPartnerId);
            this.senderPartnerSelector().setSelectedPartner(partner);
        }

        this.partnerSystems = await this.partnerSystemService.getAllPartnerSystems();
        this.initSteps = await this.initStepService.getInitSteps();

        setInterval(() => {
            if (this.formData) {
                this.formData.updateValueAndValidity(); // Hacky hacky, men det lader ikke til at valid-property automatisk opdateres på formGroup ved two-way bindings, som der bruges i DataFieldGroupContainer komponenten... Hvis nogen kan fixe det - go ahead :)
            }
        }, 800);
    }

    get fcFormatTypeId(): UntypedFormControl {
        return this.formData.get('distribution.formatTypeId') as UntypedFormControl;
    }

    get fcFormat(): UntypedFormControl {
        return this.formData.get('distribution.formatId') as UntypedFormControl;
    }

    get fcVariant(): UntypedFormControl {
        return this.formData.get('distribution.variantId') as UntypedFormControl;
    }

    get fcVariantVersion(): UntypedFormControl {
        return this.formData.get('distribution.variantVersionId') as UntypedFormControl;
    }

    get fcDocumentType(): UntypedFormControl {
        return this.formData.get('distribution.documentTypeId') as UntypedFormControl;
    }

    get fcDistributions(): UntypedFormControl {
        return this.formData.get('distribution.distributionIds') as UntypedFormControl;
    }

    get fcSenderPartnerId(): UntypedFormControl {
        return this.formData.get('sender.partnerId') as UntypedFormControl;
    }

    get fcReceiverPartnersId(): UntypedFormControl {
        return this.formData.get('receiver.partnerIds') as UntypedFormControl;
    }

    get fcPartnerSystemId(): UntypedFormControl {
        return this.formData.get('partnerSystem.partnerSystemId') as UntypedFormControl;
    }

    partialDocumentConfigs = computed(() =>
        this.docConfigIn().documentConfigs?.filter(e =>
            e.FormatId === this.initConfiguration?.FormatId &&
            e.VariantId === this.initConfiguration?.VariantId &&
            e.VariantVersionsId === this.initConfiguration?.VariantVersionId &&
            e.DistributionObjectId === this.initConfiguration?.DocumentTypeId) ?? []);

    setSelectedDocumentConfig(): void {
        const selectedDC = this.docConfigIn().documentConfigs?.find(e =>
            e.FormatId === this.initConfiguration?.FormatId &&
            e.VariantId === this.initConfiguration?.VariantId &&
            e.VariantVersionsId === this.initConfiguration?.VariantVersionId &&
            e.DistributionObjectId === this.initConfiguration?.DocumentTypeId);
        if (selectedDC) {
            void this.docConfigIn().setSelectedInternal(selectedDC.DocumentConfigId, false);
        }
    }

    async initStepChanged(initStepId: number, type: 'PreDisassemble' | 'CustomDisassemble' | 'PostDisassemble'): Promise<void> {
        switch (type) {
            case 'PreDisassemble': this.preDisassembleDisplayGroups = initStepId ? await this.getDisplayGroups(initStepId, this.initConfiguration.PreDisassembleInitStepConfig) : []; break;
            case 'CustomDisassemble': this.customDisassembleDisplayGroups = initStepId ? await this.getDisplayGroups(initStepId, this.initConfiguration.CustomDisassembleInitStepConfig) : []; break;
            case 'PostDisassemble': this.postDisassembleDisplayGroups = initStepId ? await this.getDisplayGroups(initStepId, this.initConfiguration.PostDisassembleInitStepConfig) : []; break;
        }
        this.changeDetectorRef.detectChanges(); // Necessary for form-validation not giving "expression has changed" error
    }

    private async getDisplayGroups(initStepId: number, jsonConfig: string): Promise<DataFieldGroup[]> {
        return await this.initializeService.getInitConfigInitStepDisplayGroups(initStepId, jsonConfig) as DataFieldGroup[];
    }

    selectedSenderPartnerIdChange(partnerId: number): void {
        this.fcSenderPartnerId.setValue(partnerId);
    }


    onDocConfigChange(documentConfig: DocumentConfig | null): void {
        this.fcFormat.setValue(documentConfig?.Format?.FormatsId);
        this.fcVariant.setValue(documentConfig?.Variant.VariantsId);
        this.fcVariantVersion.setValue(documentConfig?.VariantVersion.VariantVersionsId);
        this.fcDocumentType.setValue(documentConfig?.DistributionObject.Id);
        this.fcFormatTypeId.setValue(null);
        void this.updateDocumentConfigChosen();
    }

    private async updateDocumentConfigChosen(): Promise<void> {
        this.documentConfigChosen = Boolean(this.fcDocumentType.value && this.fcFormat.value && this.fcVariant.value && this.fcVariantVersion.value);

        const partialDocumentConfigChosen = !this.documentConfigChosen && Boolean(this.fcDocumentType.value || this.fcFormat.value || this.fcVariant.value || this.fcVariantVersion.value);
        if (partialDocumentConfigChosen) {
            const e: DocumentConfigSearchDisplay = {
                DocumentConfigId: -2,
                Description: null,
                DistributionObjectId: Number(this.fcDocumentType.value),
                DistributionObjectName: null,
                DirectionId: null,
                Direction: null,
                LockDirection: null,
                FormatId: Number(this.fcFormat.value),
                FormatName: null,
                VariantId: Number(this.fcVariant.value),
                VariantName: null,
                VariantVersionsId: Number(this.fcVariantVersion.value),
                VersionName: null,
            };
            await this.updateDocumentConfigEntityNames(e);
            this.docConfigIn().setSingleDocumentConfig(e);
            this.disabled = true;
        }
    }

    private async updateDocumentConfigEntityNames(item: DocumentConfigSearchDisplay): Promise<void> {
        const documentTypesPromise: Promise<DocumentTypeEntity[]> = item.DistributionObjectId ? this.documentTypeService.getDistributionObjects() : Promise.resolve<DocumentTypeEntity[]>([]);
        const formatsPromise: Promise<Format[]> = item.FormatId ? this.developerService.getFormats() : Promise.resolve<Format[]>([]);
        const variantsPromise: Promise<Variant[]> = item.VariantId ? this.developerService.getVariants() : Promise.resolve<Variant[]>([]);
        const variantVersionsPromise: Promise<VariantVersion[]> = item.VariantVersionsId ? this.developerService.getVariantVersions() : Promise.resolve<VariantVersion[]>([]);
        const [documentTypes, formats, variants, variantVersions] = await Promise.all([documentTypesPromise, formatsPromise, variantsPromise, variantVersionsPromise]);

        const unknown = '<undefined>';
        item.DistributionObjectName = item.DistributionObjectId
            ? documentTypes.find(documentType => documentType.Id === item.DistributionObjectId)?.Name ?? `${item.DistributionObjectId}`
            : unknown;
        item.FormatName = item.FormatId
            ? formats.find(format => format.FormatsId === item.FormatId)?.FormatName ?? `${item.FormatId}`
            : unknown;
        item.VariantName = item.VariantId
            ? variants.find(variant => variant.VariantsId === item.VariantId)?.VariantName ?? `${item.VariantId}`
            : unknown;
        item.VersionName = item.VariantVersionsId
            ? variantVersions.find(variantVersion => variantVersion.VariantVersionsId === item.VariantVersionsId)?.VariantVersionName ?? `${item.VariantVersionsId}`
            : unknown;

        item.DisplayName = `${item.FormatName} / ${item.VariantName} / ${item.VersionName} [${item.DistributionObjectName}]`;
    }

    onDistributionsChange(distributionIds: number[]): void {
        this.distributionsChosen = distributionIds?.length > 0;
        if (this.distributionsChosen) {
            // Reset all values that are now set by one or more distributions...
            this.fcSenderPartnerId.setValue(null);
            this.fcReceiverPartnersId.setValue([]);
            this.onDocConfigChange(null);
            this.docConfigIn().setSelected(-1);
            this.senderPartnerSelector().setSelectedPartner(null);
            this.fcDocumentType.setValue(null);
            this.fcFormatTypeId.setValue(null);
        }
    }

    async refresh(): Promise<void> {
        await Promise.all([
            this.documentTypeService.getDistributionObjects().then(result => { this.documentTypes = result; }),
            this.developerService.getFormatTypes().then(result => { this.formatTypes = result; })
        ]);
    }

    buildForm(): UntypedFormGroup {
        return this.fb.group({
            name: ['', Validators.required],
            sender: this.fb.group({
                partnerId: null
            }),
            receiver: this.fb.group({
                partnerIds: []
            }),
            distribution: this.fb.group({
                distributionIds: [],
                documentTypeId: null,

                formatId: null,
                variantId: null,
                variantVersionId: null,

                formatTypeId: null
            }),
            document: this.fb.group({
                documentNumberMacro: null
            }),
            disassemble: this.fb.group({
                preDisassembleInitStepId: null,
                customDisassembleInitStepId: null,
                postDisassembleInitStepId: null
            }),
            partnerSystem: this.fb.group({
                partnerSystemId: null
            })
        });
    }

    updateFormFromInitConfiguration(): void {
        this.formData.setValue({
            name: this.headline ?? '',
            sender: {
                partnerId: this.initConfiguration?.SenderPartnerId ?? null
            },
            receiver: {
                partnerIds: this.initConfiguration?.ReceiverPartnerIds ?? []
            },
            distribution: {
                distributionIds: this.initConfiguration?.DistributionIds ?? [],
                documentTypeId: this.initConfiguration?.DocumentTypeId ?? null,

                formatId: this.initConfiguration?.FormatId ?? null,
                variantId: this.initConfiguration?.VariantId ?? null,
                variantVersionId: this.initConfiguration?.VariantVersionId ?? null,

                formatTypeId: this.initConfiguration?.FormatTypeId ?? null
            },
            document: {
                documentNumberMacro: this.initConfiguration?.DocumentNumberMacro ?? null
            },
            disassemble: {
                preDisassembleInitStepId: this.initConfiguration?.PreDisassembleInitStepId ?? null,
                customDisassembleInitStepId: this.initConfiguration?.CustomDisassembleInitStepId ?? null,
                postDisassembleInitStepId: this.initConfiguration?.PostDisassembleInitStepId ?? null
            },
            partnerSystem: {
                partnerSystemId: this.initConfiguration?.PartnerSystemId ?? null
            }
        });

        if (this.disabled) {
            this.formData.disable();
        }
        else {
            this.formData.enable();
        }
    }

    ok(): void {
        if (this.formData.valid) {
            const dataModel = this.formData.getRawValue() as InitConfigurationFormValue;
            const initConfiguration: InitConfiguration = {
                ...this.initConfiguration,
                SenderPartnerId: dataModel.sender.partnerId,
                ReceiverPartnerIds: dataModel.receiver.partnerIds,
                DistributionIds: dataModel.distribution.distributionIds,
                DocumentTypeId: dataModel.distribution.documentTypeId,
                FormatTypeId: dataModel.distribution.formatTypeId,
                FormatId: dataModel.distribution.formatId,
                VariantId: dataModel.distribution.variantId,
                VariantVersionId: dataModel.distribution.variantVersionId,
                DocumentNumberMacro: dataModel.document.documentNumberMacro,
                PartnerSystemId: dataModel.partnerSystem.partnerSystemId,
                PreDisassembleInitStepId: dataModel.disassemble.preDisassembleInitStepId,
                PreDisassembleInitStepValues: DataFieldGroup.getDataFieldValues(this.preDisassembleDisplayGroups, false),
                CustomDisassembleInitStepId: dataModel.disassemble.customDisassembleInitStepId,
                CustomDisassembleInitStepValues: DataFieldGroup.getDataFieldValues(this.customDisassembleDisplayGroups, false),
                PostDisassembleInitStepId: dataModel.disassemble.postDisassembleInitStepId,
                PostDisassembleInitStepValues: DataFieldGroup.getDataFieldValues(this.postDisassembleDisplayGroups, false)
            };

            this.modal.close(initConfiguration);
        }
    }

    cancel(): void {
        this.modal.dismiss(ModalDismissStatus.Cancel);
    }

    delete(): void {
        this.modal.dismiss(ModalDismissStatus.Delete);
    }
}
